import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { ConsularFaqItem } from "@/content/consular-corpus/faq";
import { CONSULAR_FAQ_CATEGORIES, getConsularCategoryDef } from "@/content/consular-corpus/faq-defs";

export const Route = createFileRoute("/en/consular-legalization/faq/$category")({
  loader: async ({ params }) => {
    const [{ getConsularFaqsByCategoryEn }, { CONSULAR_KEYWORDS_EN_TOP }] = await Promise.all([
      import("@/content/consular-corpus/categories"),
      import("@/content/consular-corpus/top.gen"),
    ]);
    const def = getConsularCategoryDef(params.category);
    if (!def) throw notFound();
    return {
      def,
      faqs: getConsularFaqsByCategoryEn(def.slug),
      keywords: CONSULAR_KEYWORDS_EN_TOP.slice(0, 60),
    };
  },
  component: ConsularFaqCategoryPageEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs, keywords } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleEn} — Thai MFA Legalization FAQ (${faqs.length} answers)`,
        description: def.descEn,
        path: `/en/consular-legalization/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/consular-legalization/faq/${params.category}` }],
        keywords,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "MFA Legalization FAQ", url: `${site}/en/consular-legalization/faq` },
              { name: def.titleEn, url: `${site}/en/consular-legalization/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            inLanguage: "en",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/consular-legalization/faq/${params.category}`)) },
      ],
    };
  },
});

function ConsularFaqCategoryPageEn() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/consular-legalization/faq" className="hover:underline">MFA Legalization FAQ</a>{" "}
          <span>/</span> <span>{def.titleEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleEn}</h1>
        <p className="text-muted-foreground mb-6">{def.descEn}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> answers in this topic — written by our licensed Thai attorneys,
          updated for 2026.
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {CONSULAR_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/en/consular-legalization/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleEn}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: ConsularFaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              No answers in this topic yet — <a href="/contact" className="text-primary underline">ask our team</a>.
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Full-service consular legalization</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Certified translation → MFA legalization → destination embassy or Apostille 2026 → worldwide delivery.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Talk to our team →
          </a>
        </section>
      
        <FaqSourceNote topic="consular" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}